import React, { useEffect } from 'react';
import { ArrowLeft, Check, MapPin, Clock, Users, Sparkles, Camera, PartyPopper, Briefcase, Heart } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
interface RentalPageProps {
  onBack: () => void;
}
const images = [{
  src: 'https://cdn.magicpatterns.com/uploads/iSkjK4zBRQuxizixEBofVn/flixclub-4.JPG.webp',
  alt: 'Flex Club Neon Sign'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/4UNwJ4TeJgetawAMFVuUmG/flixclub-16.JPG.webp',
  alt: 'Feiern im Flex Club'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/hvoXa7dSkgjXyYtHmiCTpi/DSCF6571_resized.jpg.webp',
  alt: 'Fotoshooting im Studio'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/rQ7wa63ECTvmFWSGowTuGy/DSCF8824_resized.jpg.webp',
  alt: 'Kreatives Shooting'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/kJyzrtZAEnKSvbjzxaWntt/dfuns_2025-10-24_192.jpg.webp',
  alt: 'Studio Atmosphäre'
}];
const useCases = [{
  icon: Camera,
  title: 'Fotoshootings',
  desc: 'Unser lichtdurchflutetes Studio mit Spiegelwand und Aerial-Tüchern bietet den perfekten Rahmen für kreative Foto- und Videoproduktionen.',
  image: 'https://cdn.magicpatterns.com/uploads/hvoXa7dSkgjXyYtHmiCTpi/DSCF6571_resized.jpg.webp',
  price: 'ab 80 €'
}, {
  icon: PartyPopper,
  title: 'Geburtstage & JGA',
  desc: 'Feiere deinen besonderen Tag in einzigartiger Atmosphäre. Mit Disco-Kugel, Neon-Schild und Party-Vibes wird dein Event unvergesslich.',
  image: 'https://cdn.magicpatterns.com/uploads/4UNwJ4TeJgetawAMFVuUmG/flixclub-16.JPG.webp',
  price: 'ab 150 €'
}, {
  icon: Sparkles,
  title: 'Workshops & Seminare',
  desc: 'Veranstalte deinen eigenen Workshop in professioneller Umgebung. Vollständig ausgestattet mit Matten, TRX und Aerial-Equipment.',
  image: 'https://cdn.magicpatterns.com/uploads/rQ7wa63ECTvmFWSGowTuGy/DSCF8824_resized.jpg.webp',
  price: 'auf Anfrage'
}, {
  icon: Briefcase,
  title: 'Firmenevents',
  desc: 'Teambuilding mal anders: Buche das Studio für ein gemeinsames Fitness-Erlebnis mit deinem Team.',
  image: 'https://cdn.magicpatterns.com/uploads/kJyzrtZAEnKSvbjzxaWntt/dfuns_2025-10-24_192.jpg.webp',
  price: 'auf Anfrage'
}];
const features = ['Ca. 100 m² Fläche', 'Professionelle Spiegelwand', 'Aerial-Tücher & Equipment', 'TRX-Suspension-Training', 'Disco-Kugel & Ambientebeleuchtung', 'Umkleide & WC', 'Bluetooth-Soundsystem', 'Zentral in Nürnberg', 'Barrierefreier Zugang', 'Kostenlose Matten & Blöcke'];
export function Rental({
  onBack
}: RentalPageProps) {
  const {
    ref: featRef,
    isVisible: featVis
  } = useScrollAnimation();
  const {
    ref: useCaseRef,
    isVisible: useCaseVis
  } = useScrollAnimation();
  const {
    ref: galleryRef,
    isVisible: galleryVis
  } = useScrollAnimation();
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);
  return <div className="min-h-screen bg-white" data-id="element-258">
      {/* Back Button */}
      <div className="fixed top-6 left-6 z-50" data-id="element-259">
        <button onClick={onBack} className="flex items-center gap-2 bg-white/90 backdrop-blur-md text-dark-studio px-4 py-2 rounded-full shadow-lg hover:bg-peony-50 hover:text-peony-600 transition-all duration-300 font-medium text-sm" data-id="element-260">
          <ArrowLeft size={16} data-id="element-261" />
          Zurück
        </button>
      </div>

      {/* Hero */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden" data-id="element-262">
        <img src="https://cdn.magicpatterns.com/uploads/iSkjK4zBRQuxizixEBofVn/flixclub-4.JPG.webp" alt="Flex Club Studio" className="absolute inset-0 w-full h-full object-cover object-center" data-id="element-263" />
        <div className="absolute inset-0 bg-gradient-to-b from-dark-studio/60 via-dark-studio/40 to-dark-studio/70" data-id="element-264"></div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto animate-fade-in-up" data-id="element-265">
          <span className="text-peony-300 font-semibold tracking-widest uppercase text-sm mb-4 block" data-id="element-266">
            Flex Club Nürnberg
          </span>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight" data-id="element-267">
            Studio mieten
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8" data-id="element-268">
            Unser einzigartiges Boutique-Studio steht dir für dein Event,
            Shooting oder Workshop zur Verfügung.
          </p>
          <a href="mailto:hello@flexandflow-nbg.de" className="btn-primary inline-block" data-id="element-269">
            Jetzt anfragen
          </a>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-white" data-id="element-270">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-271">
          <h2 className="text-3xl md:text-4xl font-bold text-dark-studio mb-6" data-id="element-272">
            Ein Raum voller Möglichkeiten
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-273">
            Das Flex Club Studio in Nürnberg ist mehr als ein Trainingsraum – es
            ist eine Bühne für deine Ideen. Mit ca. 100 m² Fläche,
            professioneller Ausstattung und einzigartiger Atmosphäre bieten wir
            den perfekten Rahmen für Fotoshootings, Workshops,
            Geburtstagsfeiern, JGAs und vieles mehr.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500" data-id="element-274">
            <div className="flex items-center gap-2" data-id="element-275">
              <MapPin size={16} className="text-peony-500" data-id="element-276" />
              <span data-id="element-277">Musterstraße 12, 90402 Nürnberg</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-278">
              <Clock size={16} className="text-peony-500" data-id="element-279" />
              <span data-id="element-280">Flexible Buchungszeiten</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-281">
              <Users size={16} className="text-peony-500" data-id="element-282" />
              <span data-id="element-283">Bis zu 20 Personen</span>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-gray-50" data-id="element-284">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-285">
          <div className="text-center mb-14" data-id="element-286">
            <h2 className="text-3xl md:text-4xl font-bold text-dark-studio mb-4" data-id="element-287">
              Perfekt geeignet für
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto" data-id="element-288">
              Von kreativen Shootings bis zu unvergesslichen Feiern – unser
              Studio passt sich deinen Wünschen an.
            </p>
          </div>

          <div ref={useCaseRef} className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-289">
            {useCases.map((item, index) => <div key={item.title} className={`group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-1 ${useCaseVis ? 'animate-fade-in-up' : 'opacity-0'}`} style={{
            animationDelay: `${index * 120}ms`
          }} data-id="element-290">
                <div className="relative h-56 overflow-hidden" data-id="element-291">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover object-center transform group-hover:scale-105 transition-transform duration-700" data-id="element-292" />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-studio/60 to-transparent" data-id="element-293"></div>
                  <div className="absolute bottom-4 left-4 flex items-center gap-2" data-id="element-294">
                    <div className="w-9 h-9 rounded-full bg-peony-500 flex items-center justify-center text-white" data-id="element-295">
                      <item.icon size={18} data-id="element-296" />
                    </div>
                    <span className="text-white font-bold text-lg" data-id="element-297">
                      {item.title}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-peony-600 font-bold text-sm" data-id="element-298">
                    {item.price}
                  </div>
                </div>
                <div className="p-6" data-id="element-299">
                  <p className="text-gray-600 leading-relaxed" data-id="element-300">{item.desc}</p>
                  <a href="mailto:hello@flexandflow-nbg.de" className="inline-flex items-center mt-4 text-peony-600 font-semibold hover:text-peony-800 transition-colors group/link" data-id="element-301">
                    Anfragen
                    <span className="ml-1 transform group-hover/link:translate-x-1 transition-transform" data-id="element-302">
                      →
                    </span>
                  </a>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-dark-studio" data-id="element-303">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-304">
          <div className="text-center mb-14" data-id="element-305">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-306">
              Was dich erwartet
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto" data-id="element-307">
              Alles was du brauchst – voll ausgestattet und bereit für dein
              Event.
            </p>
          </div>

          <div ref={featRef} className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${featVis ? 'animate-fade-in-up' : 'opacity-0'}`} data-id="element-308">
            {features.map((feature, index) => <div key={index} className="flex items-center gap-3 bg-dark-surface p-4 rounded-xl border border-white/5 hover:border-peony-500/30 transition-colors" data-id="element-309">
                <div className="w-7 h-7 rounded-full bg-peony-500/20 flex items-center justify-center flex-shrink-0" data-id="element-310">
                  <Check size={14} className="text-peony-400" strokeWidth={3} data-id="element-311" />
                </div>
                <span className="text-gray-300 font-medium" data-id="element-312">{feature}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-white" data-id="element-313">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-314">
          <div className="text-center mb-12" data-id="element-315">
            <h2 className="text-3xl md:text-4xl font-bold text-dark-studio mb-4" data-id="element-316">
              Impressionen
            </h2>
          </div>
          <div ref={galleryRef} className={`grid grid-cols-2 md:grid-cols-3 gap-4 ${galleryVis ? 'animate-fade-in-up' : 'opacity-0'}`} data-id="element-317">
            {images.map((img, i) => <div key={i} className={`overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-500 group ${i === 0 ? 'md:col-span-2 row-span-1' : ''}`} data-id="element-318">
                <img src={img.src} alt={img.alt} className="w-full h-64 object-cover object-center transform group-hover:scale-105 transition-transform duration-700" data-id="element-319" />
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-peony-50 to-white" data-id="element-320">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-321">
          <Heart className="w-12 h-12 text-peony-400 mx-auto mb-6" data-id="element-322" />
          <h2 className="text-3xl md:text-4xl font-bold text-dark-studio mb-4" data-id="element-323">
            Bereit für dein Event?
          </h2>
          <p className="text-gray-600 mb-8 text-lg" data-id="element-324">
            Schreib uns einfach eine Nachricht mit deinem Wunschtermin und wir
            melden uns schnellstmöglich bei dir.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-325">
            <a href="mailto:hello@flexandflow-nbg.de" className="btn-primary text-center" data-id="element-326">
              E-Mail schreiben
            </a>
            <a href="https://wa.me/4991112345" target="_blank" rel="noopener noreferrer" className="px-8 py-3 border-2 border-peony-400 text-peony-600 font-semibold rounded-full hover:bg-peony-50 transition-all duration-300 text-center" data-id="element-327">
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark-studio text-center py-8 text-gray-500 text-sm border-t border-white/10" data-id="element-328">
        <p data-id="element-329">
          &copy; {new Date().getFullYear()} Flex Club Nürnberg ·{' '}
          <button onClick={onBack} className="text-peony-400 hover:text-peony-300 transition-colors" data-id="element-330">
            Zurück zur Startseite
          </button>
        </p>
      </footer>
    </div>;
}